// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useNavigate } from "react-router-dom"
import { Lock, Clock, ChefHat, Flame, ClipboardCheck, Repeat, ShieldAlert } from "lucide-react"
import type { MealInstance } from "@bitebalance/contracts"
import { useI18n, formatKcal } from "@/i18n"
import { Button } from "@/components/ui/button"
import { Chip } from "./Chip"
import { SlotGlyph } from "./SlotGlyph"
import { humanizeRecipeId, isSnackSlot, reasonBadgeLabel, slotLabel } from "./recipeDisplay"
import type { ReplacementDisplay } from "./outcomeDisplay"

export interface MealCardProps {
  meal: MealInstance
  onSwap: (meal: MealInstance) => void
  /** Persisted outcome from the day ledger (shared outcomeDisplay builder —
   * Today AND Plan consume the same state); null = unlogged. */
  loggedOutcome?: string | null
  /** partial: server-counted vs planned kcal, prebuilt copy. */
  outcomeDetail?: string | null
  /**
   * ADR-078: changed only, and only when a server-linked off-plan entry
   * replaced THIS slot — never a day-wide guess. A legacy/unlinked "changed"
   * row (null here) shows only the plain outcome badge, same as before.
   */
  outcomeReplacement?: ReplacementDisplay | null
  onMarkOutcome: (meal: MealInstance) => void
  className?: string
  /** 2026-09-02 finding C: server-authoritative — this meal violates the
   * user's CURRENT hard constraints and must not look like a normal
   * valid recommendation. */
  constraintConflict?: boolean
}

const MAX_VISIBLE_REASON_BADGES = 3

/**
 * A single planned meal (Today rows and Plan week-grid cells share this).
 * Redesigned 2026-08 (owner: "bulky texts, not styled, buttons out of
 * frames"): slot/title lead (the category-art thumbnail was removed under
 * debt 37, 2026-09-07 — it was picked by hashing the recipe id), kcal/
 * macros/time/cost render as compact chips instead of a dot-separated text
 * line, and the action row is icon-first so it never overflows the narrow
 * week-grid column (w-72 ≈ 256px of content).
 */
export function MealCard({
  meal,
  onSwap,
  onMarkOutcome,
  loggedOutcome = null,
  outcomeDetail = null,
  outcomeReplacement = null,
  className,
  constraintConflict = false,
}: MealCardProps) {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  // Server-resolved catalog title when present (MealInstance.title, nullable
  // only when the recipe row is missing); humanized id as the honest fallback.
  const title = meal.title?.[locale] ?? humanizeRecipeId(meal.recipeId)
  // ADR-078: a "changed" slot with a server-linked replacement IS the
  // replacement food now — name and chips show what was actually eaten;
  // the original recipe is demoted to one small line ("Instead of …").
  const replaced = loggedOutcome === "changed" ? outcomeReplacement : null
  const displayTitle = replaced ? replaced.description : title
  // Founder decision 2026-09-19: a changed meal (server-linked replacement
  // or a legacy/unlinked "Changed it" row) never opens a page any more —
  // "Open recipe" reading like the change was lost when tapped, per the
  // founder's report. No photo/art exists on this card to begin with
  // (Debt 37) so the OLD recipe's art never shows here either way.
  const changed = loggedOutcome === "changed"

  // Owner 2026-08-30: the old dashed/muted snack treatment read as "not
  // part of the plan". Snacks now share the solid card language — only
  // slightly more compact (smaller art + title), clearly first-class.
  const snack = isSnackSlot(meal.slot)
  const visibleReasons = meal.reasonCodes.slice(0, MAX_VISIBLE_REASON_BADGES)
  const hiddenReasonCount = meal.reasonCodes.length - visibleReasons.length

  return (
    <article
      className={`flex flex-col gap-3 rounded-bb-xl border transition-shadow ${
        snack
          ? "border-line bg-paper p-3 shadow-sm hover:shadow-md"
          : "border-line bg-paper p-4 shadow-sm hover:shadow-md"
      } ${meal.locked ? "ring-2 ring-green/35" : ""} ${
        loggedOutcome === "skipped" || loggedOutcome === "changed" ? "opacity-75" : ""
      } ${className ?? ""}`}
      aria-label={displayTitle}
    >
      <div className="flex items-start gap-3">
        {/* Debt 37: no category artwork chosen by hashing the recipe id —
            a meal with no verified image is its name and numbers. */}
        <div className="min-w-0 flex-1">
          {/* T21 (ADR-070): the slot gets a time-of-day mark beside its word, so a
              day scans as a rhythm instead of four identical text blocks. The mark
              encodes the served slot — never the food. */}
          <p className="flex items-center gap-1.5 text-[11px] font-bold tracking-wide text-green-2 uppercase">
            <SlotGlyph slot={meal.slot} className="size-3.5 shrink-0 text-green-2" />
            {slotLabel(meal.slot, t)}
          </p>
          <h3
            className={`line-clamp-2 ${snack ? "text-sm" : "text-base"} leading-snug font-semibold text-ink`}
          >
            {displayTitle}
          </h3>
        </div>
        {(meal.locked || meal.batchComponentId) && (
          <div className="flex shrink-0 flex-col items-end gap-1">
            {meal.locked && (
              <span className="inline-flex items-center gap-1 rounded-bb-full bg-green/10 px-2 py-0.5 text-xs font-medium text-green">
                <Lock className="size-3" aria-hidden="true" />
                {t("views.mealCard.lockedBadge")}
              </span>
            )}
            {meal.batchComponentId && (
              <span className="inline-flex items-center gap-1 rounded-bb-full bg-sky/30 px-2 py-0.5 text-xs font-medium text-ink-2">
                <ChefHat className="size-3" aria-hidden="true" />
                {t("views.mealCard.batchBadge")}
              </span>
            )}
          </div>
        )}
      </div>

      {visibleReasons.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="reason badges">
          {visibleReasons.map(code => (
            <li
              key={code}
              className="rounded-bb-full border border-line bg-cream px-2 py-0.5 text-xs text-ink-2"
            >
              {reasonBadgeLabel(code, t)}
            </li>
          ))}
          {hiddenReasonCount > 0 && (
            <li className="rounded-bb-full border border-line bg-cream px-2 py-0.5 text-xs text-ink-2">
              +{hiddenReasonCount}
            </li>
          )}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {replaced ? (
          // ADR-078: a replaced slot's chips collapse to the one honest figure that still applies —
          // the replacement's kcal (time/macros describe the UNEATEN planned recipe, not this food).
          <Chip icon={Flame} tone="accent">
            {replaced.estimated ? "≈ " : ""}
            {formatKcal(replaced.kcal, locale)}
          </Chip>
        ) : (
          <>
            <Chip icon={Flame} tone="accent">
              {formatKcal(meal.nutrition.kcal, locale)}
            </Chip>
            <Chip icon={Clock}>
              {t("views.mealCard.activeTime", { minutes: meal.activeTimeMinutes })} ·{" "}
              {t("views.mealCard.totalTime", { minutes: meal.totalTimeMinutes })}
            </Chip>
            {/* Owner 2026-08-30: snacks looked like second-class citizens with
                their sparse chip row — every meal now carries the same full
                treatment (macros + servings). */}
            <Chip>
              {t("views.mealCard.macroLine", {
                p: Math.round(meal.nutrition.proteinG),
                c: Math.round(meal.nutrition.carbsG),
                f: Math.round(meal.nutrition.fatG),
              })}
            </Chip>
          </>
        )}
        {constraintConflict && (
          <span
            role="status"
            className="inline-flex items-center gap-1 rounded-bb-sm bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800"
          >
            <ShieldAlert className="size-3" aria-hidden="true" />
            {t("views.plan.conflictsMealBadge")}
          </span>
        )}
        {/* 2026-09-02 final owner decision: NO portion-multiplier badge on
            meal cards — the kcal/macros/time chips already describe the
            user's portion; the multiplier is implementation math. Scaling
            semantics are unchanged server-side. */}
      </div>

      {replaced ? (
        <p className="text-xs text-ink-2">{t("views.mealCard.insteadOf", { meal: title })}</p>
      ) : null}

      <div className="flex items-center gap-1.5 pt-1">
        {changed ? (
          // Founder decision 2026-09-19: tapping a changed meal opens no page — this note
          // takes the "Open recipe" button's place instead of navigating anywhere.
          <p className="flex min-h-9 flex-1 items-center text-xs text-ink-2">
            {t("views.mealCard.changedNote")}
          </p>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="min-h-9 flex-1 justify-center"
            onClick={() => navigate(`/app/recipes/${meal.id}`)}
          >
            {t("views.mealCard.openRecipe")}
          </Button>
        )}
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="min-h-9 size-9 shrink-0"
          title={t("views.mealCard.swap")}
          aria-label={t("views.mealCard.swap")}
          onClick={() => onSwap(meal)}
        >
          <Repeat className="size-4" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="min-h-9 size-9 shrink-0"
          title={t("views.mealCard.markOutcome")}
          aria-label={t("views.mealCard.markOutcome")}
          onClick={() => onMarkOutcome(meal)}
        >
          <ClipboardCheck className="size-4" aria-hidden="true" />
        </Button>
        {/* ADR-078: the "Instead of …" line above already says this slot changed — a
            replaced row skips the redundant badge (mirrors mobile's identical row redesign). */}
        {!replaced && loggedOutcome ? (
          <span
            className={`inline-flex items-center gap-1 rounded-bb-full px-2.5 py-1 text-xs font-semibold ${
              loggedOutcome === "skipped" || loggedOutcome === "changed"
                ? "bg-cream text-ink-2"
                : "bg-green/10 text-green"
            }`}
          >
            <ClipboardCheck className="size-3.5" aria-hidden="true" />
            {t("views.mealCard.loggedAs", { outcome: t(`views.outcomeSheet.${loggedOutcome}`) })}
            {outcomeDetail ? <span className="font-normal">· {outcomeDetail}</span> : null}
          </span>
        ) : null}
      </div>
    </article>
  )
}
